import React from 'react';
import {observer} from 'mobx-react-lite';
import intl from 'react-intl-universal';
import classNames from 'classnames';
import {useMobileNavController, useRootStore, useUserState} from "@stores/AppStateContext";

import type {MuiIconExternalProps} from '../../components/icons/MuiIcon';

import {SettingsIcon} from '../../components/icons/SettingsIcon';
import {PersonAdd} from '../../components/icons/PersonAdd';
import {PersonIcon} from "../../components/icons/PersonIcon";
import {Tooltip} from '../../components/Tooltip';

import {PAGES} from '../../../stores/ViewStore';
import {screenSizeProvider} from '../../../stores/ScreenSizeProvider';

import './SettingsSecondaryNav.css';

export const SETTINGS_SECONDARY_NAV_WIDTH = 326;

type MenuItem = {
    id: string;
    title: string;
    Icon: React.ComponentType<MuiIconExternalProps>;
    page: typeof PAGES[keyof typeof PAGES];
}

const companySettings: MenuItem[] = [
    { id: 'stgGeneral', title: intl.get('settings-menu.general').d('General'), Icon: SettingsIcon, page: PAGES.SETTINGS_GENERAL },
    { id: 'stgMembers', title: intl.get('settings-menu.members').d('Members'), Icon: PersonAdd, page: PAGES.SETTINGS_MEMBERS },
    { id: 'stgProfile', title: intl.get('profile.header').d('Profile'), Icon: PersonIcon, page: PAGES.SETTINGS_PROFILE },
];

const workspaceSettings: MenuItem[] = [
    { id: 'wsGeneral', title: intl.get('settings-menu.wsGeneral').d('Workspace General Settings'), Icon: SettingsIcon, page: PAGES.WORKSPACE_SETTINGS_GENERAL },
    { id: 'wsMembers', title: intl.get('settings-menu.wsMembers').d('Workspace Members'), Icon: PersonAdd, page: PAGES.WORKSPACE_MEMBERS },
];

export const isSettingsPage = (page: string) => [
    PAGES.SETTINGS_GENERAL,
    PAGES.SETTINGS_MEMBERS,
    PAGES.SETTINGS_BILLING,
    PAGES.WORKSPACE_SETTINGS_GENERAL,
    PAGES.WORKSPACE_MEMBERS,
].includes(page as any);

export const SettingsSecondaryNav = observer(() => {
    const rootStore = useRootStore();
    const userState = useUserState();
    const mobileNav = useMobileNavController();
    const isMobile = screenSizeProvider.view !== 'desktop';
    // On mobile, show as an overlay layer
    if (isMobile && !mobileNav.showSettingsSecondary) return null;

    const root = rootStore;
    const active = root.view.page;

    const handleClick = (page: MenuItem['page']) => () => {
        root.view.openPageByName(page);
    }

    return (
        <aside className={classNames('SettingsSecondaryNav', 'theme-dark', {
            'SettingsSecondaryNav--overlay': isMobile,
        })}
        >
            <div className="SettingsSecondaryNav__section title-medium">
                {intl.get('settings-menu.system').d('System')}
            </div>
            {companySettings.map(mi => (
                <SettingsSecondaryNavItem
                    key={mi.id}
                    item={mi}
                    isActive={active === mi.page}
                    onClick={() => {
                        handleClick(mi.page)();
                        if (isMobile) mobileNav.openPage();
                    }}
                />
            ))}

            {userState?.availableWorkspaces.length ? (
                <>
                    <div className="SettingsSecondaryNav__section title-medium">
                        {intl.get('Ws.context-menu.addMembers.WorkspaceSettings').d('Workspace Settings')}
                    </div>
                    {workspaceSettings.map(mi => (
                        <SettingsSecondaryNavItem
                            key={mi.id}
                            item={mi}
                            isActive={active === mi.page}
                            onClick={() => {
                                handleClick(mi.page)();
                                if (isMobile) mobileNav.openPage();
                            }}
                        />
                    ))}
                </>
            ) : null}
        </aside>
    );
});

const SettingsSecondaryNavItem = observer(({ item, isActive, onClick }: {
    item: MenuItem;
    isActive: boolean;
    onClick: () => void;
}) => {
    const textRef = React.useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = React.useState(false);

    React.useEffect(() => {
        if (textRef.current) {
            setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
        }
    }, []);

    const Icon = item.Icon;

    return (
        <div
            className={classNames('SettingsSecondaryNav_item title-small', { 'SettingsSecondaryNav_item--selected': isActive })}
            onClick={onClick}
        >
            <Icon className='SettingsSecondaryNav_icon' size={24} />
            <span ref={textRef} className='SettingsSecondaryNav_text'>
                {isTruncated ? (
                    <Tooltip maxWidth={300} title={item.title}>
                        <span>{item.title}</span>
                    </Tooltip>
                ) : (
                    item.title
                )}
            </span>
        </div>
    );
});
